(function () {
  'use strict';
  if (window.LPMobileVideoBanner) return;
  const instances = new WeakMap();

  function enhance(banner) {
    if (instances.has(banner)) return;
    const video = banner.querySelector('video');
    const button = banner.querySelector('[data-hp-banner-play]');
    if (!video || !button) return;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let fullscreen = false;
    let dialog = null;
    let marker = null;
    let disposed = false;

    function play() {
      const attempt = video.play();
      if (attempt?.catch) attempt.catch(() => {}); // The poster and fullscreen button remain available.
    }

    function inlinePlayback() {
      if (fullscreen || disposed) return;
      video.muted = true;
      video.loop = true;
      video.controls = false;
      button.tabIndex = 0;
      if (visible && !reducedMotion.matches) play();
      else video.pause();
    }

    function setFullscreen(enabled) {
      fullscreen = enabled;
      banner.classList.toggle('is-mobile-fullscreen', enabled);
      button.tabIndex = enabled ? -1 : 0;
      if (!enabled) modeChanged();
    }

    function restoreDialog() {
      if (!dialog) return;
      const closed = dialog;
      dialog = null;
      if (marker?.parentNode) marker.replaceWith(video);
      marker = null;
      closed.remove();
      setFullscreen(false);
      button.focus({ preventScroll: true });
    }

    function openDialog() {
      if (disposed || dialog) return;
      dialog = document.createElement('dialog');
      if (typeof dialog.showModal !== 'function') {
        dialog = null;
        video.controls = true;
        play();
        return;
      }
      dialog.className = 'hp-mobile-video-dialog';
      dialog.setAttribute('aria-label', 'Fullscreen video');
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'hp-mobile-video-dialog__close';
      close.setAttribute('aria-label', 'Close video');
      close.textContent = '\u00d7';
      close.addEventListener('click', () => dialog?.close());
      dialog.addEventListener('close', restoreDialog, { once: true });
      marker = document.createComment('mobile-video-position');
      video.before(marker);
      dialog.append(video, close);
      document.body.append(dialog);
      dialog.showModal();
      setFullscreen(true);
      video.controls = true;
      play();
      close.focus();
    }

    function enterFullscreen(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      video.muted = false;
      video.controls = true;
      play();
      // Invoke inside this user gesture; awaiting play() first loses activation on iOS.
      try {
        if (typeof video.webkitEnterFullscreen === 'function') video.webkitEnterFullscreen();
        else if (typeof video.requestFullscreen === 'function') {
          const attempt = video.requestFullscreen();
          if (attempt?.catch) attempt.catch(openDialog);
        } else openDialog();
      } catch (_) { openDialog(); }
    }

    function fullscreenChange() {
      const active = document.fullscreenElement === video || document.webkitFullscreenElement === video;
      if (active) setFullscreen(true);
      else if (fullscreen && !dialog) setFullscreen(false);
    }

    function beginWebkitFullscreen() { setFullscreen(true); }
    function endWebkitFullscreen() { setFullscreen(false); }
    function syncButton() {
      button.tabIndex = fullscreen ? -1 : 0;
    }

    function modeChanged() {
      button.setAttribute('aria-label', 'Watch video fullscreen');
      inlinePlayback();
    }

    const observer = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
      inlinePlayback();
    }, { threshold: 0.15 });
    observer.observe(banner);
    button.addEventListener('click', enterFullscreen, true);
    video.addEventListener('play', syncButton);
    video.addEventListener('webkitbeginfullscreen', beginWebkitFullscreen);
    video.addEventListener('webkitendfullscreen', endWebkitFullscreen);
    document.addEventListener('fullscreenchange', fullscreenChange);
    document.addEventListener('webkitfullscreenchange', fullscreenChange);
    reducedMotion.addEventListener('change', inlinePlayback);
    instances.set(banner, () => {
      disposed = true;
      observer.disconnect();
      if (dialog) { dialog.close(); restoreDialog(); }
      video.pause();
      button.removeEventListener('click', enterFullscreen, true);
      video.removeEventListener('play', syncButton);
      video.removeEventListener('webkitbeginfullscreen', beginWebkitFullscreen);
      video.removeEventListener('webkitendfullscreen', endWebkitFullscreen);
      document.removeEventListener('fullscreenchange', fullscreenChange);
      document.removeEventListener('webkitfullscreenchange', fullscreenChange);
      reducedMotion.removeEventListener('change', inlinePlayback);
      instances.delete(banner);
    });
    modeChanged();
  }

  function init(root = document) { root.querySelectorAll('[data-hp-mobile-banner]').forEach(enhance); }
  window.LPMobileVideoBanner = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-hp-mobile-banner]').forEach(banner => instances.get(banner)?.());
  });
})();
